/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import {
	PAGE_DATA_LAYER,
} from '@/data/constants/gtm';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { CommonDataPayload,  PageDataPayload, UserDataPayload } from '@/data/types/GTM';
import { error as logError } from '@/data/utils/loggerUtil';
import TagManager from 'react-gtm-module';
import { getGTMCommonEventData, getGTMUserDataEventData, getGTMXPageViewEventData } from '../../data/gtm/GACommonData';

/**
 * Measure sending all common needed data for GTM on Every page load from the Footer.
 * @param data transformed input payload from common event (see data-type)
 */
export const measure_GA4_Common = async (
	data: Awaited<ReturnType<typeof getGTMCommonEventData>>
) => {
	const {  storeId, language, environment, currency, locale, country, device} = data;
	const dataLayerName = PAGE_DATA_LAYER;
	// Send StoreId
	const storeIdTagManagerArgs = {
		dataLayer: {
			"storeId": storeId
		},
		dataLayerName,
	};
	TagManager.dataLayer(storeIdTagManagerArgs);
	// Send language
	const languageTagManagerArgs = {
		dataLayer: {
			"language": language
		},
		dataLayerName,
	};
	TagManager.dataLayer(languageTagManagerArgs);
	// Send environment
	const environmentTagManagerArgs = {
		dataLayer: {
			"environment": environment
		},
		dataLayerName,
	};
	TagManager.dataLayer(environmentTagManagerArgs);
	// Send currency
	const currencyTagManagerArgs = {
		dataLayer: {
			"currency": currency
		},
		dataLayerName,
	};
	TagManager.dataLayer(currencyTagManagerArgs);
	// Send locale
	const localeTagManagerArgs = {
		dataLayer: {
			"locale": locale
		},
		dataLayerName,
	};
	TagManager.dataLayer(localeTagManagerArgs);
	// Send country
	const countryTagManagerArgs = {
		dataLayer: {
			"country": country
		},
		dataLayerName,
	};
	TagManager.dataLayer(countryTagManagerArgs);
	// Send device
	const deviceTagManagerArgs = {
		dataLayer: {
			"device": device
		},
		dataLayerName,
	};
	TagManager.dataLayer(deviceTagManagerArgs);
};
export const measure_GA4_XPage = async (
	data: Awaited<ReturnType<typeof getGTMXPageViewEventData>>
) => {
	const {pageName, pageType } = data;
	const dataLayerName = PAGE_DATA_LAYER;
	// Send pageName
	const pageNameTagManagerArgs = {
		dataLayer: {
			"pageName": pageName
		},
		dataLayerName,
	};
	TagManager.dataLayer(pageNameTagManagerArgs); 
	// Send pageType
	const pageTypeTagManagerArgs = {
		dataLayer: {
			"pageType": pageType
		},
		dataLayerName,
	};
	TagManager.dataLayer(pageTypeTagManagerArgs); 
};
export const measure_GA4_User = async (
	data: Awaited<ReturnType<typeof getGTMUserDataEventData>>
) => {
	const { userId, userType  } = data;
	const dataLayerName = PAGE_DATA_LAYER;
	// Send pageType
	const userIdTagManagerArgs = {
		dataLayer: {
			"userId": userId
		},
		dataLayerName,
	};
	TagManager.dataLayer(userIdTagManagerArgs); 
	// Send userType
	const userTypeTagManagerArgs = {
		dataLayer: {
			"userType": userType
		},
		dataLayerName,
	};
	TagManager.dataLayer(userTypeTagManagerArgs); 
};

export const sendGTMCommonviewEvent = async (payload: CommonDataPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);

	if (ua || ga4) {
		const data = await getGTMCommonEventData(payload);

		if (ga4) {
			try {
				await measure_GA4_Common(data);
			} catch (error) {
				logError(undefined, 'AddToCart: sendGTMAddToCartEvent: measure_GA4: error: %o', error);
			}
		}
	}
}

export const sendGTMXPageViewEvent = async (payload: PageDataPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);

	if (ua || ga4) {
		const data = await getGTMXPageViewEventData(payload);

		if (ga4) {
			try {
				await measure_GA4_XPage(data);
			} catch (error) {
				logError(undefined, 'AddToCart: sendGTMAddToCartEvent: measure_GA4: error: %o', error);
			}
		}
	}
}

export const sendGTMUserDataEvent = async (payload: UserDataPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);

	if (ua || ga4) {
		const data = await getGTMUserDataEventData(payload);

		if (ga4) {
			try {
				await measure_GA4_User(data);
			} catch (error) {
				logError(undefined, 'AddToCart: sendGTMAddToCartEvent: measure_GA4: error: %o', error);
			}
		}
	}
}
